'use client';

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useMagneticCursor } from '@/hooks/useMagneticCursor';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  magnetic?: boolean;
  children: React.ReactNode;
  className?: string;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  'data-ph-capture'?: boolean;
}

const variants = {
  primary:
    'bg-zibarit-bone text-zibarit-negro hover:bg-white',
  secondary:
    'border border-current text-current hover:bg-zibarit-negro hover:text-zibarit-bone',
  ghost:
    'text-current underline-offset-4 hover:underline',
  dark:
    'bg-zibarit-negro text-zibarit-bone hover:bg-zibarit-negro/85',
};

const sizes = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
};

export function Button({
  variant = 'primary',
  size = 'md',
  magnetic = false,
  children,
  className,
  href,
  target,
  rel,
  onClick,
  type = 'button',
  'data-ph-capture': dataPhCapture,
}: ButtonProps) {
  const { ref, springX, springY, handleMouseMove, handleMouseLeave } =
    useMagneticCursor(0.3);

  const baseClass = cn(
    'inline-flex items-center justify-center font-lato font-bold uppercase tracking-wider rounded-full transition-colors duration-300',
    variants[variant],
    sizes[size],
    className,
  );

  const phAttr = dataPhCapture ? { 'data-ph-capture': true as const } : {};

  const content = magnetic ? (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: springX, y: springY }}
      className="inline-block"
    >
      {href ? (
        <a href={href} target={target} rel={rel} onClick={onClick} className={baseClass} {...phAttr}>
          {children}
        </a>
      ) : (
        <button type={type} onClick={onClick} className={baseClass} {...phAttr}>
          {children}
        </button>
      )}
    </motion.div>
  ) : href ? (
    <a href={href} target={target} rel={rel} onClick={onClick} className={baseClass} {...phAttr}>
      {children}
    </a>
  ) : (
    <button type={type} onClick={onClick} className={baseClass} {...phAttr}>
      {children}
    </button>
  );

  return content;
}
